'use client';

import { useContext, useEffect, useState, type FC } from 'react';
import {
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Select,
  Text,
  Toggle,
} from '@/_components';
import { getDocuments, moveItem } from '@/_api/client-documents';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import type { Company } from '@/_types/models/company';
import type { FileOrFolderMovingOrCopyingDialogProps } from './FileOrFolderMovingOrCopyingDialog.types';

export const FileOrFolderMovingOrCopyingDialog: FC<
  FileOrFolderMovingOrCopyingDialogProps
> = ({ companyId, isMoving, isOpen, selectedItems, onClose, onUpdated }) => {
  const [newLocation, setNewLocation] = useState<string>('home/');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const { addToast } = useContext(ToastContext);
  const { handleIncomingPath, openDirectory, reloadFiles, setOpenDirectory } =
    useContext(DocumentsContext);
  const { token } = useContext(UserContext);
  const { selectedCompanyRelatedCompanies } = useContext(CompanyContext);
  const [results, setResults] = useState<any[]>([]);
  const [entityIdBeingChangedTo, setEntityIdBeingChangedTo] = useState<
    string | undefined
  >(undefined);
  const [isChangingEntity, setIsChangingEntity] = useState<boolean>(false);

  const isSingleItem = selectedItems.length === 1;

  useEffect(() => {
    if (selectedCompanyRelatedCompanies?.length > 1) {
      const id = selectedCompanyRelatedCompanies.find(
        (company: Company) => company.id === companyId,
      )?.id;

      if (id) {
        setEntityIdBeingChangedTo(String(id));
      }
    }
  }, [selectedCompanyRelatedCompanies, companyId]);

  const onUpdate = async () => {
    try {
      const nextLocation =
        newLocation === 'home/' ? '/' : newLocation.split('home')[1];

      setIsUpdating(true);

      for (const item of selectedItems) {
        const newPath =
          (nextLocation.endsWith('/') ? nextLocation : nextLocation + '/') +
          item.name;
        const oldPath =
          (openDirectory.endsWith('/') ? openDirectory : openDirectory + '/') +
          item.name;

        const isFolder = item.type === 'folder';

        const params: any = {
          companyId,
          deleteOldPath: isMoving === true,
          newPath,
          oldPath,
          isFolder,
          extractedDocumentId: item?.extracted_document_id,
          token,
        };

        if (
          entityIdBeingChangedTo &&
          companyId &&
          entityIdBeingChangedTo !== String(companyId)
        ) {
          params.newCompanyId = Number(entityIdBeingChangedTo);
        }

        await moveItem(params);
      }

      let nextOpenDirectory = nextLocation === '/' ? '' : nextLocation;

      if (entityIdBeingChangedTo) {
        nextOpenDirectory = '';
      }

      setOpenDirectory(nextOpenDirectory);

      await handleIncomingPath(nextOpenDirectory);

      // So when the location is 2 levels deep, it actually opens that in directory tree (left-hand side)
      if (nextLocation.split('/').length > 2) {
        await reloadFiles(nextOpenDirectory);
      }

      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onClose();
      console.log('closing');
      setIsUpdating(false);
    }
  };

  const updateItemsWithFolders = async (path: string) => {
    try {
      setIsLoading(true);
      const nextPath = ['/', ''].includes(path) ? '' : path;

      const { documents } = await getDocuments({
        companyId: entityIdBeingChangedTo
          ? Number(entityIdBeingChangedTo)
          : companyId,
        path: nextPath,
        token,
      });

      const folders = documents
        .filter((doc) => {
          if (doc.type !== 'folder') return false;

          // We don't want to move a folder into itself
          if (
            nextPath === openDirectory &&
            isSingleItem &&
            selectedItems[0].type === 'folder' &&
            doc.name === selectedItems[0].name
          ) {
            return false;
          }

          return true;
        })
        .map((doc) => ({
          name: doc.name,
          value: doc.name,
        }));

      folders.unshift({ name: '(Choose folder)', value: '' });
      setResults(folders);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (entityIdBeingChangedTo) {
      updateItemsWithFolders('');
      setNewLocation('home/');
    }
  }, [entityIdBeingChangedTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof openDirectory === 'string') {
      setNewLocation('home' + openDirectory);
      const path = openDirectory.startsWith('/')
        ? openDirectory
        : '/' + openDirectory;
      updateItemsWithFolders(path);
    }
  }, [openDirectory]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Specify new location">
      <Dialog.Content>
        <Flex isColumn gap={4}>
          <Flex yAlign="baseline" gap={4}>
            <Text>
              {isMoving ? 'Move' : 'Copy'}{' '}
              <strong>
                {isSingleItem
                  ? selectedItems[0].name
                  : `${selectedItems.length} items`}
              </strong>{' '}
              to
            </Text>

            <Flex>
              {newLocation.split('/').map((part, index) => (
                <Flex key={index} gap={1} yAlign="baseline">
                  {index < newLocation.split('/').length - 1 ? (
                    <>
                      <Flex
                        onClick={async () => {
                          if (index === 0) {
                            await updateItemsWithFolders('');
                            setNewLocation('home/');
                          } else {
                            const path = newLocation
                              .split('/')
                              .slice(0, index + 1)
                              .join('/');

                            await updateItemsWithFolders(path.split('home')[1]);
                            setNewLocation(path);
                          }
                        }}
                      >
                        <Text
                          className="cursor-pointer"
                          type="small"
                          color="accent"
                        >
                          {part === 'home' ? '(Home)' : part}
                        </Text>
                      </Flex>

                      <Text type="small" color="secondary">
                        {' '}
                      </Text>
                      <Text type="small" color="secondary">
                        {'/'}
                      </Text>
                      <Text type="small" color="secondary">
                        {' '}
                      </Text>
                    </>
                  ) : (
                    <Text type="small" color="primary">
                      {part === 'home' ? '(Home)' : part}
                    </Text>
                  )}
                </Flex>
              ))}
            </Flex>
          </Flex>

          <Select
            items={results}
            label="Select Location"
            placeholder="Select Location"
            isLabelHidden={true}
            isDisabled={isLoading || isUpdating}
            onChange={({ target: { value } }) => {
              if (value === '') {
                return;
              }

              const nextLocation = newLocation.endsWith('/')
                ? newLocation + value
                : newLocation + '/' + value;
              setNewLocation(nextLocation);
              updateItemsWithFolders(nextLocation.split('home')[1]);
            }}
            selectedValue={''}
          />

          {!!selectedCompanyRelatedCompanies && (
            <Flex paddingX={4} gap={4} isColumn>
              <Toggle
                isChecked={isChangingEntity}
                label="Different entity"
                onChange={() => {
                  setIsChangingEntity((prev) => !prev);
                }}
              />

              {isChangingEntity && (
                <Flex yAlign="baseline" gap={4}>
                  <Text>{isMoving ? 'Move' : 'Copy'} to </Text>

                  <Select
                    items={selectedCompanyRelatedCompanies?.map((company) => ({
                      name: company.name,
                      value: String(company.id),
                    }))}
                    label="Select Entity"
                    placeholder="Select Entity"
                    isLabelHidden={true}
                    isDisabled={isLoading || isUpdating}
                    onChange={({ target: { value } }) => {
                      setEntityIdBeingChangedTo(value);
                    }}
                    selectedValue={entityIdBeingChangedTo}
                  />
                </Flex>
              )}
            </Flex>
          )}

          <Flex gap={4} isColumn>
            <ButtonGroup align="right">
              <Button
                intent="primary"
                isDisabled={isLoading || isUpdating}
                isLoading={isUpdating}
                onClick={onUpdate}
                value={isMoving ? 'Move' : 'Copy'}
              />

              <Button
                intent="danger-secondary"
                isDisabled={isLoading || isUpdating}
                onClick={onClose}
                value="Cancel"
              />
            </ButtonGroup>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
